"use client";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  Package,
  Clock,
  AlertTriangle,
  CheckCircle,
} from "lucide-react";
import { useState } from "react";

// Sample data for loads created over months
const loadsCreatedData = [
  { month: "Aug 2024", loads: 45, target: 50 },
  { month: "Sep 2024", loads: 52, target: 50 },
  { month: "Oct 2024", loads: 38, target: 50 },
  { month: "Nov 2024", loads: 67, target: 50 },
  { month: "Dec 2024", loads: 71, target: 50 },
  { month: "Jan 2025", loads: 58, target: 50 },
];

// Sample data for late drop-offs percentage
const lateDropoffData = [
  {
    month: "Aug 2024",
    latePercentage: 12.5,
    onTimePercentage: 87.5,
    totalLoads: 45,
  },
  {
    month: "Sep 2024",
    latePercentage: 8.2,
    onTimePercentage: 91.8,
    totalLoads: 52,
  },
  {
    month: "Oct 2024",
    latePercentage: 15.8,
    onTimePercentage: 84.2,
    totalLoads: 38,
  },
  {
    month: "Nov 2024",
    latePercentage: 6.1,
    onTimePercentage: 93.9,
    totalLoads: 67,
  },
  {
    month: "Dec 2024",
    latePercentage: 9.8,
    onTimePercentage: 90.2,
    totalLoads: 71,
  },
  {
    month: "Jan 2025",
    latePercentage: 11.2,
    onTimePercentage: 88.8,
    totalLoads: 58,
  },
];

// Current month summary data
const currentMonthSummary = {
  totalLoads: 58,
  onTimeDeliveries: 51,
  lateDeliveries: 7,
  averageDelay: 2.3, // hours
};

const COLORS = {
  onTime: "#10b981",
  late: "#ef4444",
  primary: "#3b82f6",
  secondary: "#8b5cf6",
};

export default function AnalyticsPage() {
  const [timeRange, setTimeRange] = useState("6months");
  const [selectedMetric, setSelectedMetric] = useState("loads");

  const getFilteredData = () => {
    const monthsToShow =
      timeRange === "3months" ? 3 : timeRange === "12months" ? 12 : 6;
    return {
      loadsData: loadsCreatedData.slice(-monthsToShow),
      lateData: lateDropoffData.slice(-monthsToShow),
    };
  };

  const { loadsData, lateData } = getFilteredData();

  // Calculate trends
  const currentMonthLoads = loadsData[loadsData.length - 1]?.loads || 0;
  const previousMonthLoads = loadsData[loadsData.length - 2]?.loads || 0;
  const loadsTrend =
    ((currentMonthLoads - previousMonthLoads) / previousMonthLoads) * 100;

  const currentMonthLate = lateData[lateData.length - 1]?.latePercentage || 0;
  const previousMonthLate = lateData[lateData.length - 2]?.latePercentage || 0;
  const lateTrend = currentMonthLate - previousMonthLate;

  return (
    <div className="min-h-screen  py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Freight Analytics
          </h1>
          <p className="text-gray-600 mt-2">
            Track performance metrics and delivery insights
          </p>
        </div>

        {/* Time Range Selector */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="3months">Last 3 Months</SelectItem>
                <SelectItem value="6months">Last 6 Months</SelectItem>
                <SelectItem value="12months">Last 12 Months</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Loads (Jan)
              </CardTitle>
              <Package className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {currentMonthSummary.totalLoads}
              </div>
              <div className="flex items-center text-xs text-muted-foreground">
                {loadsTrend > 0 ? (
                  <TrendingUp className="h-3 w-3 text-green-500 mr-1" />
                ) : (
                  <TrendingDown className="h-3 w-3 text-red-500 mr-1" />
                )}
                <span
                  className={loadsTrend > 0 ? "text-green-500" : "text-red-500"}
                >
                  {Math.abs(loadsTrend).toFixed(1)}%
                </span>
                <span className="ml-1">from last month</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                On-Time Deliveries
              </CardTitle>
              <CheckCircle className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {currentMonthSummary.onTimeDeliveries}
              </div>
              <p className="text-xs text-muted-foreground">
                {(
                  (currentMonthSummary.onTimeDeliveries /
                    currentMonthSummary.totalLoads) *
                  100
                ).toFixed(1)}
                % success rate
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Late Deliveries
              </CardTitle>
              <AlertTriangle className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {currentMonthSummary.lateDeliveries}
              </div>
              <div className="flex items-center text-xs text-muted-foreground">
                {lateTrend < 0 ? (
                  <TrendingDown className="h-3 w-3 text-green-500 mr-1" />
                ) : (
                  <TrendingUp className="h-3 w-3 text-red-500 mr-1" />
                )}
                <span
                  className={lateTrend < 0 ? "text-green-500" : "text-red-500"}
                >
                  {Math.abs(lateTrend).toFixed(1)}%
                </span>
                <span className="ml-1">from last month</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Avg Delay</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {currentMonthSummary.averageDelay}h
              </div>
              <p className="text-xs text-muted-foreground">
                Average delay when late
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Loads Created Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Loads Created Over Time</CardTitle>
              <CardDescription>Monthly load creation vs target</CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer
                config={{
                  loads: {
                    label: "Loads Created",
                    color: COLORS.primary,
                  },
                  target: {
                    label: "Target",
                    color: COLORS.secondary,
                  },
                }}
                className="h-[300px]"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={loadsData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="month"
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar
                      dataKey="loads"
                      fill="var(--color-loads)"
                      name="Loads Created"
                    />
                    <Bar
                      dataKey="target"
                      fill="var(--color-target)"
                      name="Target"
                      opacity={0.6}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>

          {/* Late Drop-offs Percentage Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Late Drop-off Percentage</CardTitle>
              <CardDescription>
                Percentage of late deliveries by month
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer
                config={{
                  latePercentage: {
                    label: "Late %",
                    color: COLORS.late,
                  },
                }}
                className="h-[300px]"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={lateData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="month"
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis
                      domain={[0, 20]}
                      tickFormatter={(value) => `${value}%`}
                    />
                    <ChartTooltip
                      content={<ChartTooltipContent />}
                      formatter={(value: any) => [
                        `${value}%`,
                        "Late Deliveries",
                      ]}
                    />
                    <Line
                      type="monotone"
                      dataKey="latePercentage"
                      stroke="var(--color-latePercentage)"
                      strokeWidth={3}
                      dot={{
                        fill: "var(--color-latePercentage)",
                        strokeWidth: 2,
                        r: 4,
                      }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>
        </div>

        {/* Detailed Performance Table */}
        <Card>
          <CardHeader>
            <CardTitle>Monthly Performance Breakdown</CardTitle>
            <CardDescription>
              Detailed view of loads and delivery performance
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">Month</th>
                    <th className="text-right p-2">Total Loads</th>
                    <th className="text-right p-2">Target</th>
                    <th className="text-right p-2">On Time</th>
                    <th className="text-right p-2">Late</th>
                    <th className="text-right p-2">Late %</th>
                    <th className="text-right p-2">Performance</th>
                  </tr>
                </thead>
                <tbody>
                  {loadsData.map((item, index) => {
                    const lateInfo = lateData[index];
                    const lateCount = Math.round(
                      (lateInfo.latePercentage / 100) * item.loads
                    );
                    const onTimeCount = item.loads - lateCount;
                    const performance = item.loads >= item.target ? "✅" : "⚠️";

                    return (
                      <tr
                        key={item.month}
                        className="border-b hover:bg-gray-50"
                      >
                        <td className="p-2 font-medium">{item.month}</td>
                        <td className="text-right p-2">{item.loads}</td>
                        <td className="text-right p-2">{item.target}</td>
                        <td className="text-right p-2 text-green-600">
                          {onTimeCount}
                        </td>
                        <td className="text-right p-2 text-red-600">
                          {lateCount}
                        </td>
                        <td className="text-right p-2">
                          <span
                            className={`px-2 py-1 rounded text-xs ${
                              lateInfo.latePercentage < 10
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {lateInfo.latePercentage.toFixed(1)}%
                          </span>
                        </td>
                        <td className="text-right p-2">{performance}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
